"use client"

import type { ComponentProps, ReactElement } from "react"
import type { Option } from "#components/option"

import React, { cloneElement, useEffect, useRef, useState } from "react"

import { twMerge } from "#lib/twMergeConfig"

// Height is optional and gives the wrapper a fixed height - otherwise it automatically calcs the childrens height - neccessary because the children are absolute and therefore the section container has height 0
export function MultiStepView({
  currentStep,
  className,
  children,
  height,
  viewStyles,
  hiddenStyles,
  exitStyles,
  initialHeight,
}: {
  children: ReactElement<ComponentProps<typeof Option>>[]
  currentStep: number
  className?: string
  viewStyles?: string
  hiddenStyles?: string
  exitStyles?: string
  height?: number
  initialHeight?: number
}) {
  const observer = useRef<ResizeObserver>()

  // Need to calculate content height because of absolute layout
  const [contentHeight, setContentHeight] = useState<number | undefined>(initialHeight ?? height)
  useEffect(() => {
    if (height) return

    const activeElement = document.querySelector("section[data-input=multistep]")?.children.item(currentStep - 1) as HTMLElement | undefined

    setContentHeight(activeElement?.offsetHeight ?? 0)

    function handleResize() {
      setContentHeight(activeElement?.offsetHeight ?? 0)
    }

    if (observer.current) {
      observer.current.disconnect()
    }

    if (activeElement) {
      observer.current = new ResizeObserver(handleResize)
      observer.current.observe(activeElement)
    }

    return () => {
      if (observer.current) observer.current.disconnect()
    }
  }, [height, currentStep])

  return (
    <section data-input={"multistep"} className={twMerge("relative h-full w-full transition-[height] duration-500", className)} style={{ height: contentHeight }}>
      {React.Children.map(children, (view, index) =>
        cloneElement(view, {
          key: view.props.identifier,
          className: twMerge(
            "left-0 w-full",
            viewStyles,
            view.props.className,
            "absolute transition-[width,opacity,left]",
            currentStep !== index + 1 && "pointer-events-none opacity-0",
            currentStep !== index + 1 && hiddenStyles,
            currentStep > index + 1 && exitStyles,
          ),
        }),
      )}
    </section>
  )
}
